import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { getTradeJournal } from '@/utils/localStorage';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend } from 'recharts';
import { TrendingUp, Target, Award, Activity, Radio, Lightbulb, AlertTriangle, Info, CheckCircle, Clock, BarChart3, Zap } from 'lucide-react';

export default function PerformanceTracker() {
  const [trades, setTrades] = useState([]);
  const [stats, setStats] = useState(null);
  const [signalPerformance, setSignalPerformance] = useState([]);
  const [cumulativeData, setCumulativeData] = useState([]);
  
  useEffect(() => {
    loadPerformanceData();
  }, []);
  
  const loadPerformanceData = () => {
    const allTrades = getTradeJournal();
    
    // Filter only closed trades with results
    const closedTrades = allTrades.filter(t => t.result && (t.result === 'win' || t.result === 'loss'));
    setTrades(closedTrades);
    
    // Calculate overall statistics
    const totalTrades = closedTrades.length;
    const wins = closedTrades.filter(t => t.result === 'win').length;
    const losses = closedTrades.filter(t => t.result === 'loss').length;
    const winRate = totalTrades > 0 ? (wins / totalTrades) * 100 : 0;
    
    // Calculate average R:R
    const rrValues = closedTrades
      .filter(t => t.riskRewardRatio)
      .map(t => parseFloat(t.riskRewardRatio));
    const avgRR = rrValues.length > 0 
      ? rrValues.reduce((a, b) => a + b, 0) / rrValues.length 
      : 0;
    
    // Calculate profit/loss
    let totalPL = 0;
    closedTrades.forEach(trade => {
      if (trade.result === 'win') {
        const reward = parseFloat(trade.rewardPercent) || 0;
        totalPL += reward;
      } else if (trade.result === 'loss') {
        const risk = parseFloat(trade.riskPercent) || 0;
        totalPL -= risk;
      }
    });
    
    const avgPL = totalTrades > 0 ? totalPL / totalTrades : 0;
    
    // Signal Quality Learning Analysis
    const bySignalType = analyzeByDimension(closedTrades, 'setupType');
    const byTimeframe = analyzeByDimension(closedTrades, 'timeframe');
    const byAsset = analyzeByDimension(closedTrades, 'asset');
    
    // Find best timeframe for each signal type
    const signalTimeframeAnalysis = {};
    ['pullback', 'breakout', 'reversal', 'continuation'].forEach(signalType => {
      const signalTrades = closedTrades.filter(t => 
        t.setupType && t.setupType.toLowerCase() === signalType
      );
      
      if (signalTrades.length > 0) {
        const tfAnalysis = analyzeByDimension(signalTrades, 'timeframe');
        const bestTF = tfAnalysis.reduce((best, current) => 
          parseFloat(current.winRate) > parseFloat(best.winRate) ? current : best
        );
        signalTimeframeAnalysis[signalType] = bestTF;
      }
    });
    
    // Generate recommendations
    const recommendations = generateRecommendations(bySignalType, byTimeframe, byAsset, signalTimeframeAnalysis);
    
    setStats({
      totalTrades,
      wins,
      losses,
      winRate: winRate.toFixed(1),
      avgRR: avgRR.toFixed(2),
      totalPL: totalPL.toFixed(2),
      avgPL: avgPL.toFixed(2),
      bySignalType,
      byTimeframe,
      byAsset,
      signalTimeframeAnalysis,
      recommendations
    });
    
    // Calculate signal type performance for chart
    const signalTypes = {};
    closedTrades.forEach(trade => {
      const type = trade.setupType || 'unknown';
      if (!signalTypes[type]) {
        signalTypes[type] = {
          total: 0,
          wins: 0,
          losses: 0,
          totalPL: 0
        };
      }
      signalTypes[type].total++;
      if (trade.result === 'win') {
        signalTypes[type].wins++;
        signalTypes[type].totalPL += parseFloat(trade.rewardPercent) || 0;
      } else {
        signalTypes[type].losses++;
        signalTypes[type].totalPL -= parseFloat(trade.riskPercent) || 0;
      }
    });
    
    const signalPerfArray = Object.entries(signalTypes).map(([type, data]) => ({
      type: type.charAt(0).toUpperCase() + type.slice(1),
      winRate: data.total > 0 ? ((data.wins / data.total) * 100).toFixed(1) : 0,
      trades: data.total,
      totalPL: data.totalPL.toFixed(2)
    }));
    
    setSignalPerformance(signalPerfArray);
    
    // Calculate cumulative P&L
    let cumulative = 0;
    const cumulativeArray = closedTrades.map((trade, index) => {
      if (trade.result === 'win') {
        cumulative += parseFloat(trade.rewardPercent) || 0;
      } else {
        cumulative -= parseFloat(trade.riskPercent) || 0;
      }
      return {
        trade: index + 1,
        cumulative: parseFloat(cumulative.toFixed(2))
      };
    });
    
    setCumulativeData(cumulativeArray);
  };
  
  const analyzeByDimension = (trades, dimension) => {
    const grouped = {};
    
    trades.forEach(trade => {
      let key;
      if (dimension === 'setupType') {
        key = (trade.setupType || 'unknown').toLowerCase();
      } else if (dimension === 'timeframe') {
        key = trade.timeframe || 'unknown';
      } else if (dimension === 'asset') {
        key = trade.asset || 'unknown';
      }
      
      if (!grouped[key]) {
        grouped[key] = {
          total: 0,
          wins: 0,
          losses: 0,
          totalPL: 0,
          rrValues: []
        };
      }
      
      grouped[key].total++;
      if (trade.result === 'win') {
        grouped[key].wins++;
        grouped[key].totalPL += parseFloat(trade.rewardPercent) || 0;
      } else {
        grouped[key].losses++;
        grouped[key].totalPL -= parseFloat(trade.riskPercent) || 0;
      }
      
      if (trade.riskRewardRatio) {
        grouped[key].rrValues.push(parseFloat(trade.riskRewardRatio));
      }
    });
    
    return Object.entries(grouped).map(([key, data]) => ({
      name: key.charAt(0).toUpperCase() + key.slice(1),
      total: data.total,
      winRate: ((data.wins / data.total) * 100).toFixed(1),
      avgPL: (data.totalPL / data.total).toFixed(2),
      avgRR: data.rrValues.length > 0 
        ? (data.rrValues.reduce((a, b) => a + b, 0) / data.rrValues.length).toFixed(2)
        : 'N/A',
      totalPL: data.totalPL.toFixed(2)
    })).sort((a, b) => parseFloat(b.winRate) - parseFloat(a.winRate));
  };
  
  const generateRecommendations = (bySignalType, byTimeframe, byAsset, signalTimeframeAnalysis) => {
    const recs = [];
    
    // Best signal type recommendation
    if (bySignalType.length > 0) {
      const best = bySignalType[0];
      if (best.total >= 3 && parseFloat(best.winRate) >= 55) {
        const bestTF = signalTimeframeAnalysis[best.name.toLowerCase()];
        if (bestTF && bestTF.name !== 'Unknown') {
          recs.push({
            type: 'success',
            message: `${best.name} signals on ${bestTF.name} timeframe show highest win rate (${best.winRate}%). Consider prioritizing these setups.`
          });
        } else {
          recs.push({
            type: 'success',
            message: `${best.name} signals show highest win rate (${best.winRate}%) with ${best.total} trades. Consider prioritizing these setups.`
          });
        }
      }
    }
    
    // Worst signal type warning
    if (bySignalType.length > 1) {
      const worst = bySignalType[bySignalType.length - 1];
      if (worst.total >= 3 && parseFloat(worst.winRate) < 40) {
        recs.push({
          type: 'warning',
          message: `${worst.name} signals have low win rate (${worst.winRate}%). Consider avoiding or reviewing these setups.`
        });
      }
    }
    
    // Best timeframe recommendation
    if (byTimeframe.length > 0) {
      const bestTF = byTimeframe[0];
      if (bestTF.total >= 3 && parseFloat(bestTF.winRate) >= 55) {
        recs.push({
          type: 'info',
          message: `${bestTF.name} timeframe performs best overall (${bestTF.winRate}% win rate, ${bestTF.total} trades).`
        });
      }
    }
    
    // Best asset recommendation
    if (byAsset.length > 0) {
      const bestAsset = byAsset[0];
      if (bestAsset.total >= 3 && parseFloat(bestAsset.winRate) >= 55) {
        recs.push({
          type: 'info',
          message: `${bestAsset.name} shows strongest performance (${bestAsset.winRate}% win rate). Focus on this asset.`
        });
      }
    }
    
    // Low sample size warning
    const totalTrades = bySignalType.reduce((sum, s) => sum + s.total, 0);
    if (totalTrades < 10) {
      recs.push({
        type: 'warning',
        message: `Only ${totalTrades} trades logged. Collect more data (minimum 20-30 trades) for reliable insights.`
      });
    }
    
    return recs;
  };
  
  const getWinRateColor = (winRate) => {
    const rate = parseFloat(winRate);
    if (rate >= 60) return '#10B981'; // success
    if (rate >= 45) return '#3B82F6'; // primary
    if (rate >= 30) return '#F59E0B'; // warning
    return '#EF4444'; // destructive
  };
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-2">
          <Target className="w-10 h-10 text-primary" />
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-foreground">
            Signal Insights
          </h1>
        </div>
        <p className="text-muted-foreground font-medium">
          Analyze historical trades to discover which signals, timeframes, and assets perform best.
        </p>
      </div>
      
      {stats && stats.totalTrades > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card className="p-6 border-border/40 bg-card">
              <div className="flex items-center space-x-2 mb-1">
                <Activity className="w-4 h-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Total Trades</p>
              </div>
              <p className="text-3xl font-black font-mono" data-testid="total-closed-trades">{stats.totalTrades}</p>
              <p className="text-xs text-muted-foreground mt-1">{stats.wins}W / {stats.losses}L</p>
            </Card>
            
            <Card className="p-6 border-border/40 bg-card">
              <div className="flex items-center space-x-2 mb-1">
                <Award className="w-4 h-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Win Rate</p>
              </div>
              <p className="text-3xl font-black font-mono text-primary" data-testid="overall-winrate">{stats.winRate}%</p>
            </Card>
            
            <Card className="p-6 border-border/40 bg-card">
              <div className="flex items-center space-x-2 mb-1">
                <TrendingUp className="w-4 h-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Avg Risk:Reward</p>
              </div>
              <p className="text-3xl font-black font-mono text-foreground" data-testid="avg-rr">{stats.avgRR}</p>
            </Card>
            
            <Card className={`p-6 border-border/40 ${parseFloat(stats.totalPL) >= 0 ? 'bg-success/10' : 'bg-destructive/10'}`}>
              <div className="flex items-center space-x-2 mb-1">
                <Activity className="w-4 h-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Total P&L</p>
              </div>
              <p className={`text-3xl font-black font-mono ${parseFloat(stats.totalPL) >= 0 ? 'text-success' : 'text-destructive'}`} data-testid="total-pl">
                {parseFloat(stats.totalPL) >= 0 ? '+' : ''}{stats.totalPL}%
              </p>
              <p className="text-xs text-muted-foreground mt-1">Avg: {parseFloat(stats.avgPL) >= 0 ? '+' : ''}{stats.avgPL}% per trade</p>
            </Card>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <Card className="p-6 border-border/40 bg-card">
              <h2 className="text-xl font-bold text-foreground mb-4">Cumulative Profit Curve</h2>
              {cumulativeData.length > 0 && (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={cumulativeData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis 
                      dataKey="trade" 
                      stroke="#A1A1AA"
                      label={{ value: 'Trade #', position: 'insideBottom', offset: -5, fill: '#A1A1AA' }}
                    />
                    <YAxis 
                      stroke="#A1A1AA"
                      label={{ value: 'Cumulative P&L (%)', angle: -90, position: 'insideLeft', fill: '#A1A1AA' }}
                    />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#18181B', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                      labelStyle={{ color: '#FAFAFA' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="cumulative" 
                      stroke="#3B82F6" 
                      strokeWidth={2}
                      dot={{ fill: '#3B82F6', r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Card>
            
            <Card className="p-6 border-border/40 bg-card">
              <h2 className="text-xl font-bold text-foreground mb-4">Signal Type Performance</h2>
              {signalPerformance.length > 0 && (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={signalPerformance}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey="type" stroke="#A1A1AA" />
                    <YAxis 
                      stroke="#A1A1AA"
                      label={{ value: 'Win Rate (%)', angle: -90, position: 'insideLeft', fill: '#A1A1AA' }}
                    />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#18181B', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                      labelStyle={{ color: '#FAFAFA' }}
                    />
                    <Bar dataKey="winRate" radius={[8, 8, 0, 0]}>
                      {signalPerformance.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getWinRateColor(entry.winRate)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>
          </div>
          
          {/* Recommendations Box */}
          {stats.recommendations && stats.recommendations.length > 0 && (
            <Card className="p-6 border-border/40 bg-gradient-to-br from-primary/5 to-primary/10 mb-6" data-testid="recommendations-box">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <Lightbulb className="w-5 h-5 text-primary" />
                </div>
                <h2 className="text-xl font-bold text-foreground">Signal Quality Insights</h2>
              </div>
              <div className="space-y-3">
                {stats.recommendations.map((rec, index) => (
                  <div 
                    key={index}
                    data-testid={`recommendation-${index}`}
                    className={`p-4 rounded-lg border flex items-start space-x-3 ${
                      rec.type === 'success' 
                        ? 'bg-success/10 border-success/30' 
                        : rec.type === 'warning' 
                        ? 'bg-warning/10 border-warning/30'
                        : 'bg-primary/10 border-primary/30'
                    }`}
                  >
                    {rec.type === 'success' ? (
                      <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                    ) : rec.type === 'warning' ? (
                      <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                    ) : (
                      <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    )}
                    <p className="text-sm font-medium text-foreground">{rec.message}</p>
                  </div>
                ))}
              </div>
            </Card>
          )}
          
          {/* Signal Insights Section */}
          <div className="mb-6">
            <div className="flex items-center space-x-3 mb-4">
              <Zap className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-foreground">Signal Insights</h2>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Best/Worst Performing Signal Types */}
              <Card className="p-6 border-border/40 bg-card" data-testid="signal-type-insights">
                <div className="flex items-center space-x-2 mb-4">
                  <Radio className="w-5 h-5 text-primary" />
                  <h3 className="text-lg font-bold text-foreground">Signal Type Performance</h3>
                </div>
                
                {stats.bySignalType && stats.bySignalType.length > 0 ? (
                  <div className="space-y-4">
                    {/* Best Performing */}
                    <div className="p-4 rounded-lg bg-success/10 border border-success/30">
                      <p className="text-xs font-semibold text-success uppercase tracking-wider mb-2">Best Performing</p>
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-bold text-foreground">{stats.bySignalType[0].name}</span>
                        <div className="text-right">
                          <span className="text-2xl font-black text-success">{stats.bySignalType[0].winRate}%</span>
                          <p className="text-xs text-muted-foreground">{stats.bySignalType[0].total} trades</p>
                        </div>
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-muted-foreground">Avg R:R:</span>
                          <span className="ml-1 font-mono font-semibold">{stats.bySignalType[0].avgRR}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Avg P&L:</span>
                          <span className={`ml-1 font-mono font-semibold ${parseFloat(stats.bySignalType[0].avgPL) >= 0 ? 'text-success' : 'text-destructive'}`}>
                            {parseFloat(stats.bySignalType[0].avgPL) >= 0 ? '+' : ''}{stats.bySignalType[0].avgPL}%
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Worst Performing */}
                    {stats.bySignalType.length > 1 && (
                      <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/30">
                        <p className="text-xs font-semibold text-destructive uppercase tracking-wider mb-2">Worst Performing</p>
                        <div className="flex items-center justify-between">
                          <span className="text-lg font-bold text-foreground">{stats.bySignalType[stats.bySignalType.length - 1].name}</span>
                          <div className="text-right">
                            <span className="text-2xl font-black text-destructive">{stats.bySignalType[stats.bySignalType.length - 1].winRate}%</span>
                            <p className="text-xs text-muted-foreground">{stats.bySignalType[stats.bySignalType.length - 1].total} trades</p>
                          </div>
                        </div>
                        <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-muted-foreground">Avg R:R:</span>
                            <span className="ml-1 font-mono font-semibold">{stats.bySignalType[stats.bySignalType.length - 1].avgRR}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Avg P&L:</span>
                            <span className={`ml-1 font-mono font-semibold ${parseFloat(stats.bySignalType[stats.bySignalType.length - 1].avgPL) >= 0 ? 'text-success' : 'text-destructive'}`}>
                              {parseFloat(stats.bySignalType[stats.bySignalType.length - 1].avgPL) >= 0 ? '+' : ''}{stats.bySignalType[stats.bySignalType.length - 1].avgPL}%
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* All Signal Types Table */}
                    <div className="mt-4">
                      <p className="text-sm font-semibold text-muted-foreground mb-2">All Signal Types</p>
                      <div className="space-y-2">
                        {stats.bySignalType.map((signal, idx) => (
                          <div 
                            key={idx}
                            className="flex items-center justify-between p-2 rounded bg-secondary/30 hover:bg-secondary/50 transition-colors"
                          >
                            <span className="font-medium text-sm">{signal.name}</span>
                            <div className="flex items-center space-x-4">
                              <span className="text-xs text-muted-foreground">{signal.total} trades</span>
                              <span 
                                className="font-mono font-bold text-sm"
                                style={{ color: getWinRateColor(signal.winRate) }}
                              >
                                {signal.winRate}%
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No signal type data available</p>
                )}
              </Card>
              
              {/* Best Timeframe for Each Signal Type */}
              <Card className="p-6 border-border/40 bg-card" data-testid="timeframe-insights">
                <div className="flex items-center space-x-2 mb-4">
                  <Clock className="w-5 h-5 text-primary" />
                  <h3 className="text-lg font-bold text-foreground">Best Timeframe by Signal</h3>
                </div>
                
                {stats.signalTimeframeAnalysis && Object.keys(stats.signalTimeframeAnalysis).length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(stats.signalTimeframeAnalysis).map(([signalType, data]) => (
                      <div 
                        key={signalType}
                        className="p-4 rounded-lg bg-secondary/30 border border-border/40"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-foreground capitalize">{signalType}</span>
                          <span className="px-2 py-1 rounded bg-primary/20 text-primary text-xs font-bold">
                            {data.name}
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div>
                            <span className="text-muted-foreground">Win Rate:</span>
                            <span className="ml-1 font-mono font-bold" style={{ color: getWinRateColor(data.winRate) }}>
                              {data.winRate}%
                            </span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Trades:</span>
                            <span className="ml-1 font-mono font-semibold">{data.total}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Avg R:R:</span>
                            <span className="ml-1 font-mono font-semibold">{data.avgRR}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 rounded-lg bg-secondary/20 text-center">
                    <Clock className="w-8 h-8 mx-auto mb-2 text-muted-foreground opacity-50" />
                    <p className="text-sm text-muted-foreground">
                      Add timeframe data to your trades to see which timeframes work best for each signal type.
                    </p>
                  </div>
                )}
                
                {/* Overall Timeframe Performance */}
                {stats.byTimeframe && stats.byTimeframe.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-border/40">
                    <p className="text-sm font-semibold text-muted-foreground mb-2">Overall Timeframe Performance</p>
                    <div className="space-y-2">
                      {stats.byTimeframe.slice(0, 5).map((tf, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center justify-between p-2 rounded bg-secondary/20 hover:bg-secondary/40 transition-colors"
                        >
                          <span className="font-medium text-sm">{tf.name}</span>
                          <div className="flex items-center space-x-4">
                            <span className="text-xs text-muted-foreground">{tf.total} trades</span>
                            <span 
                              className="font-mono font-bold text-sm"
                              style={{ color: getWinRateColor(tf.winRate) }}
                            >
                              {tf.winRate}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            </div>
          </div>
          
          {/* Top Performing Assets */}
          <Card className="p-6 border-border/40 bg-card mb-6" data-testid="asset-insights">
            <div className="flex items-center space-x-2 mb-4">
              <BarChart3 className="w-5 h-5 text-primary" />
              <h3 className="text-lg font-bold text-foreground">Top Performing Assets</h3>
            </div>
            
            {stats.byAsset && stats.byAsset.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {stats.byAsset.slice(0, 6).map((asset, idx) => (
                  <div 
                    key={idx}
                    className={`p-4 rounded-lg border ${
                      idx === 0 
                        ? 'bg-success/10 border-success/30' 
                        : 'bg-secondary/30 border-border/40'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-foreground">{asset.name}</span>
                      {idx === 0 && (
                        <span className="px-2 py-0.5 rounded-full bg-success/20 text-success text-xs font-bold">
                          TOP
                        </span>
                      )}
                    </div>
                    <div className="flex items-end justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">{asset.total} trades</p>
                        <p className="text-xs text-muted-foreground">Avg R:R: {asset.avgRR}</p>
                      </div>
                      <div className="text-right">
                        <span 
                          className="text-2xl font-black"
                          style={{ color: getWinRateColor(asset.winRate) }}
                        >
                          {asset.winRate}%
                        </span>
                        <p className={`text-xs font-mono ${parseFloat(asset.totalPL) >= 0 ? 'text-success' : 'text-destructive'}`}>
                          {parseFloat(asset.totalPL) >= 0 ? '+' : ''}{asset.totalPL}%
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No asset data available</p>
            )}
          </Card>
          
          {/* Signal Performance Table */}
          <Card className="p-6 border-border/40 bg-card">
            <h2 className="text-xl font-bold text-foreground mb-4">Complete Signal Breakdown</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-border/40">
                  <tr className="text-left">
                    <th className="p-4 font-bold text-sm text-muted-foreground">Signal Type</th>
                    <th className="p-4 font-bold text-sm text-muted-foreground">Total Trades</th>
                    <th className="p-4 font-bold text-sm text-muted-foreground">Win Rate</th>
                    <th className="p-4 font-bold text-sm text-muted-foreground">Total P&L</th>
                  </tr>
                </thead>
                <tbody>
                  {signalPerformance.map((signal, index) => (
                    <tr key={index} className="border-b border-border/20 hover:bg-accent/50 transition-colors">
                      <td className="p-4">
                        <div className="flex items-center space-x-2">
                          <Radio className="w-4 h-4 text-primary" />
                          <span className="font-semibold text-foreground">{signal.type}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="font-mono font-semibold">{signal.trades}</span>
                      </td>
                      <td className="p-4">
                        <span 
                          className="font-mono font-bold text-lg"
                          style={{ color: getWinRateColor(signal.winRate) }}
                        >
                          {signal.winRate}%
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`font-mono font-bold ${parseFloat(signal.totalPL) >= 0 ? 'text-success' : 'text-destructive'}`}>
                          {parseFloat(signal.totalPL) >= 0 ? '+' : ''}{signal.totalPL}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      ) : (
        <Card className="p-12 border-border/40 bg-card text-center">
          <Target className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-20" />
          <p className="text-muted-foreground font-medium mb-2">No closed trades yet</p>
          <p className="text-sm text-muted-foreground">
            Start logging trades with results (win/loss) in your Trade Journal to see performance analytics.
          </p>
        </Card>
      )}
    </div>
  );
}
